if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              // Push GTM add to cart event
              this.pushGTMAddToCartEvent(formData.get('id'), response);
              
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      async pushGTMAddToCartEvent(variantId, cartResponse) {
        try {
          // Initialize dataLayer if it doesn't exist
          window.dataLayer = window.dataLayer || [];

          // Get product information from cart response or fetch it
          let productData = null;
          const quantityInput = this.form.querySelector('[name="quantity"]');
          const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

          // Try to get product info from cart response
          if (cartResponse && cartResponse.items) {
            const addedItem = cartResponse.items.find(item => item.variant_id.toString() === variantId.toString());
            if (addedItem) {
              productData = {
                id: addedItem.product_id.toString(),
                name: addedItem.product_title,
                variant_id: addedItem.variant_id.toString(),
                variant_name: addedItem.variant_title || 'Default',
                price: (addedItem.final_price / 100).toFixed(2),
                quantity: addedItem.quantity
              };
            }
          }

          // If not found in cart response, try to get from form or page
          if (!productData) {
            // Try to get product info from the page
            const productForm = this.closest('[data-product-id]') || this.closest('product-info');
            if (productForm) {
              const productId = productForm.dataset.productId;
              const productTitle = document.querySelector('h1.product__title')?.textContent?.trim() || 
                                 document.querySelector('[data-product-title]')?.dataset.productTitle ||
                                 'Unknown Product';
              const priceElement = document.querySelector('.price--on-sale .price-item--regular, .price .price-item--regular, [data-price]');
              const price = priceElement ? parseFloat(priceElement.textContent.replace(/[^0-9.]/g, '')) : 0;
              
              productData = {
                id: productId || variantId,
                name: productTitle,
                variant_id: variantId.toString(),
                variant_name: 'Default',
                price: price.toFixed(2),
                quantity: quantity
              };
            }
          }

          // Fallback: fetch product info from variant
          if (!productData) {
            try {
              const variantResponse = await fetch(`/variants/${variantId}.js`);
              if (variantResponse.ok) {
                const variant = await variantResponse.json();
                const productResponse = await fetch(`/products/${variant.product_id}.js`);
                if (productResponse.ok) {
                  const product = await productResponse.json();
                  productData = {
                    id: product.id.toString(),
                    name: product.title,
                    variant_id: variant.id.toString(),
                    variant_name: variant.title || 'Default',
                    price: (variant.price / 100).toFixed(2),
                    quantity: quantity
                  };
                }
              }
            } catch (e) {
              console.warn('Could not fetch product data for GTM:', e);
            }
          }

          // Push to GTM dataLayer
          if (productData) {
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                currency: cartResponse?.currency || 'USD',
                value: parseFloat(productData.price) * productData.quantity,
                items: [{
                  item_id: productData.id,
                  item_name: productData.name,
                  item_variant: productData.variant_id,
                  item_variant_name: productData.variant_name,
                  price: parseFloat(productData.price),
                  quantity: productData.quantity
                }]
              }
            });
          }
        } catch (error) {
          console.warn('GTM add to cart tracking error:', error);
        }
      }
    }
  );
}
